import { generateSaturatedColor } from '../../Utils/colorUtils';

const DEFAULT_TAG_COLOR = '#5757b0';
const TAG_BOX_PADDING = 0.008;
const SPLIT_REVIEW_TAG_VARIABLE = '__dataunfold_split_review__';
export const VIEWER_TAG_HOVER_EVENT = 'dataunfold:viewer-tag-hover';

const clamp01 = (value) => Math.max(0, Math.min(1, Number(value)));

const normalizeComparableValue = (value) => (
  value === null || value === undefined ? '' : String(value).trim()
);

const normalizeComparableVariable = (value) => normalizeComparableValue(value).toLowerCase();

const normalizeVisionOrientation = (value) => {
  const normalized = String(value || '').trim().toLowerCase()
    .replace(/-/g, '_')
    .replace(/\s+/g, '_');
  if (['', '0', 'upright', 'none', 'no_rotation', 'off'].includes(normalized)) return '0';
  if (['90', '90_degrees', '90_cw', 'rotate_90', 'rotate_90_cw'].includes(normalized)) return '90_cw';
  if (['180', '180_degrees', 'rotate_180'].includes(normalized)) return '180';
  if (['270', '270_degrees', '90_ccw', 'rotate_270', 'rotate_90_ccw'].includes(normalized)) return '90_ccw';
  return null;
};

const visionOrientationToDegrees = {
  '0': 0,
  '90_cw': 90,
  '180': 180,
  '90_ccw': 270,
};

const parseObjectValue = (value) => {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value;
  }
  if (typeof value !== 'string') return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
};

const getPersistedSplitReview = (timeevent, fields) => {
  const directReview = parseObjectValue(timeevent?.splitReview ?? timeevent?.split_review);
  if (directReview) return directReview;

  const reviewField = fields.find((field) => (
    normalizeComparableVariable(field?.variable) === SPLIT_REVIEW_TAG_VARIABLE
  ));
  if (!reviewField) return null;
  return parseObjectValue(reviewField?.reply ?? reviewField?.response);
};

const getPersistedRotation = (value) => {
  const persistedCorrection = value?.correctionRotation
    ?? value?.correction_rotation
    ?? value?.rotation
    ?? value?.orientation;
  const orientation = normalizeVisionOrientation(persistedCorrection);
  if (orientation) return visionOrientationToDegrees[orientation] || 0;

  const numericRotation = ((Number(persistedCorrection) || 0) % 360 + 360) % 360;
  return [0, 90, 180, 270].includes(numericRotation) ? numericRotation : null;
};

const getOptionalPersistedRotation = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const orientation = normalizeVisionOrientation(value);
  if (orientation) return visionOrientationToDegrees[orientation] || 0;
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return null;
  const numericRotation = ((numericValue % 360) + 360) % 360;
  return [0, 90, 180, 270].includes(numericRotation) ? numericRotation : null;
};

const getPersistedBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  return ['1', 'true', 'yes', 'on'].includes(String(value || '').trim().toLowerCase());
};

const normalizeCoordinateItems = (coordinates) => {
  if (!coordinates) return [];
  if (typeof coordinates === 'string') {
    try {
      coordinates = JSON.parse(coordinates);
    } catch (error) {
      return [];
    }
  }
  return Array.isArray(coordinates) ? coordinates : [coordinates];
};

export const getVisionOrientationByPage = (timeevent) => {
  const orientationByPage = {};
  const fields = Array.isArray(timeevent?.customFields) ? timeevent.customFields : [];
  const persistedReview = getPersistedSplitReview(timeevent, fields);
  const persistedOrientations = parseObjectValue(persistedReview?.orientation_by_page)
    || persistedReview?.orientation_by_page;
  const persistedEntries = Array.isArray(persistedOrientations)
    ? persistedOrientations.map((value) => [value?.page ?? value?.page_number, value])
    : Object.entries(persistedOrientations || {});
  if (persistedOrientations && typeof persistedOrientations === 'object') {
    persistedEntries.forEach(([rawPage, value]) => {
      const page = Number(rawPage);
      const rotation = getPersistedRotation(value);
      const fineAngle = Number(value?.fineAngle ?? value?.fine_angle ?? 0);
      if (!Number.isInteger(page) || page < 1 || rotation == null) return;
      const persistedOrientation = {
        rotation,
        fineAngle: Number.isFinite(fineAngle) ? fineAngle : 0,
      };
      const hasExplicitRotationContract = [
        'correctionRotation',
        'correction_rotation',
        'pdfRotation',
        'pdf_rotation',
        'finalRotation',
        'final_rotation',
        'rotationBakedIntoPdf',
        'rotation_baked_into_pdf',
        'coordinateSpace',
        'coordinate_space',
      ].some((key) => Object.prototype.hasOwnProperty.call(value || {}, key));
      if (hasExplicitRotationContract) {
        persistedOrientation.correctionRotation = rotation;
        persistedOrientation.pdfRotation = getOptionalPersistedRotation(
          value?.pdfRotation ?? value?.pdf_rotation,
        );
        persistedOrientation.finalRotation = getOptionalPersistedRotation(
          value?.finalRotation ?? value?.final_rotation,
        );
        persistedOrientation.rotationBakedIntoPdf = getPersistedBoolean(
          value?.rotationBakedIntoPdf ?? value?.rotation_baked_into_pdf,
        );
        persistedOrientation.coordinateSpace = String(
          value?.coordinateSpace
          ?? value?.coordinate_space
          ?? 'pdf_rendered_before_correction',
        );
      }
      orientationByPage[page] = persistedOrientation;
    });
  }

  fields.forEach((field) => {
    const fieldVariable = normalizeComparableVariable(field?.variable);
    if (fieldVariable === SPLIT_REVIEW_TAG_VARIABLE || fieldVariable.startsWith('parent_')) return;
    normalizeCoordinateItems(field?.coordinates).forEach((coordinate) => {
      if (!coordinate || typeof coordinate !== 'object') return;
      const page = Number(coordinate.page || 1);
      if (!Number.isInteger(page) || page < 1) return;

      const orientation = normalizeVisionOrientation(coordinate.vision_orientation);
      const fineAngle = Number(coordinate.vision_fine_angle || 0);
      if (!orientation && (!Number.isFinite(fineAngle) || Math.abs(fineAngle) < 0.001)) return;

      const nextOrientation = {
        rotation: visionOrientationToDegrees[orientation || '0'] || 0,
        fineAngle: Number.isFinite(fineAngle) ? fineAngle : 0,
      };
      const currentOrientation = orientationByPage[page];
      if (!currentOrientation || (
        currentOrientation.rotation === 0
        && Math.abs(currentOrientation.fineAngle) < 0.001
        && (nextOrientation.rotation !== 0 || Math.abs(nextOrientation.fineAngle) >= 0.001)
      )) {
        orientationByPage[page] = nextOrientation;
      }
    });
  });

  return orientationByPage;
};

export const buildTagHoverEventDetail = (
  tag,
  timeevent = null,
  occurrenceIndex = null,
  origin = 'tag',
) => {
  if (!tag) {
    return null;
  }

  return {
    eventId: tag?.timeeventid ?? tag?.timeeventId ?? timeevent?.id ?? timeevent?.timeeventid ?? null,
    tagId: tag?.id ?? null,
    tagVariable: tag?.variable || '',
    occurrenceIndex: Number.isInteger(occurrenceIndex) && occurrenceIndex >= 0 ? occurrenceIndex : null,
    origin,
  };
};

export const doesTagHoverDetailMatch = (detail, tag, timeevent = null) => {
  if (!detail || !tag) {
    return false;
  }

  const detailEventId = normalizeComparableValue(detail.eventId);
  const tagEventId = normalizeComparableValue(
    timeevent?.id ?? timeevent?.timeeventid ?? tag?.timeeventid ?? tag?.timeeventId
  );

  if (!detailEventId || !tagEventId || detailEventId !== tagEventId) {
    return false;
  }

  const detailTagId = normalizeComparableValue(detail.tagId);
  const tagId = normalizeComparableValue(tag?.id);
  if (detailTagId && tagId && detailTagId === tagId) {
    return true;
  }

  const detailTagVariable = normalizeComparableVariable(detail.tagVariable);
  const tagVariable = normalizeComparableVariable(tag?.variable);
  return Boolean(detailTagVariable && tagVariable && detailTagVariable === tagVariable);
};

const hslToRgba = (color, alpha) => {
  const match = String(color || '').match(/hsl\(\s*(-?\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%\s*\)/i);
  if (!match) {
    return `rgba(87, 87, 176, ${alpha})`;
  }

  let h = Number(match[1]) % 360;
  if (h < 0) h += 360;
  const s = Number(match[2]) / 100;
  const l = Number(match[3]) / 100;
  const chroma = (1 - Math.abs(2 * l - 1)) * s;
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - chroma / 2;
  let r = 0;
  let g = 0;
  let b = 0;

  if (h < 60) [r, g, b] = [chroma, x, 0];
  else if (h < 120) [r, g, b] = [x, chroma, 0];
  else if (h < 180) [r, g, b] = [0, chroma, x];
  else if (h < 240) [r, g, b] = [0, x, chroma];
  else if (h < 300) [r, g, b] = [x, 0, chroma];
  else [r, g, b] = [chroma, 0, x];

  return `rgba(${Math.round((r + m) * 255)}, ${Math.round((g + m) * 255)}, ${Math.round((b + m) * 255)}, ${alpha})`;
};

export const getTagHighlightColor = (label) => (
  label ? generateSaturatedColor(String(label)) : DEFAULT_TAG_COLOR
);

export const getTagHighlightBackgroundColor = (label, alpha = 0.12) => (
  hslToRgba(getTagHighlightColor(label), alpha)
);

export const padNormalizedBbox = (bbox, padding = TAG_BOX_PADDING) => {
  if (!Array.isArray(bbox) || bbox.length !== 4) {
    return null;
  }

  const [x0, y0, x1, y1] = bbox.map(clamp01);
  return [
    clamp01(x0 - padding),
    clamp01(y0 - padding),
    clamp01(x1 + padding),
    clamp01(y1 + padding),
  ];
};

export const rotateNormalizedBbox = (bbox, rotationDegrees = 0) => {
  if (!Array.isArray(bbox) || bbox.length !== 4) {
    return null;
  }

  const normalizedRotation = ((Number(rotationDegrees) || 0) % 360 + 360) % 360;
  const [x0, y0, x1, y1] = bbox.map(clamp01);
  const corners = [
    [x0, y0],
    [x1, y0],
    [x1, y1],
    [x0, y1],
  ];

  let mappedCorners;
  if (normalizedRotation === 90) {
    mappedCorners = corners.map(([x, y]) => [1 - y, x]);
  } else if (normalizedRotation === 180) {
    mappedCorners = corners.map(([x, y]) => [1 - x, 1 - y]);
  } else if (normalizedRotation === 270) {
    mappedCorners = corners.map(([x, y]) => [y, 1 - x]);
  } else {
    mappedCorners = corners;
  }

  const xs = mappedCorners.map(([x]) => clamp01(x));
  const ys = mappedCorners.map(([, y]) => clamp01(y));
  return [
    Math.min(...xs),
    Math.min(...ys),
    Math.max(...xs),
    Math.max(...ys),
  ];
};
